import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import agentApiService from '../../services/agentApiService';

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
const fmtDate = (d) => {
  if (!d) return null;
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return String(d); }
};

const fmtCancelDate = (d, offsetHrs) => {
  if (!d) return null;
  try {
    const dt = new Date(d);
    if (offsetHrs) dt.setHours(dt.getHours() - offsetHrs);
    return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return String(d); }
};

const rupee = (v) =>
  v != null && v !== '' && !isNaN(v)
    ? `₹ ${parseFloat(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
    : null;

const statusMeta = (s) => {
  switch (s?.toUpperCase()) {
    case 'BOOKED':    return { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' };
    case 'CANCELLED': return { cls: 'bg-red-100 text-red-700 border-red-200',             dot: 'bg-red-500'     };
    case 'HOLD':      return { cls: 'bg-amber-100 text-amber-700 border-amber-200',        dot: 'bg-amber-500'   };
    case 'PENDING':   return { cls: 'bg-blue-100 text-blue-700 border-blue-200',           dot: 'bg-blue-500'    };
    default:          return { cls: 'bg-gray-100 text-gray-600 border-gray-200',           dot: 'bg-gray-400'    };
  }
};

const decodeHtml = (html) => {
  if (!html) return '';
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
};

/* Helper to parse amenities whether they come as a JSON string, comma-separated string, array of strings, or array with bracketed string */
const parseAmenities = (amenities) => {
  if (!amenities) return [];
  if (Array.isArray(amenities)) {
    // Check if single string inside array like ["[\"Wifi\", \"AC\"]"] or ["Wifi, AC"]
    if (amenities.length === 1 && typeof amenities[0] === 'string') {
      let str = amenities[0].trim();
      if ((str.startsWith('[') && str.endsWith(']')) || (str.startsWith('{') && str.endsWith('}'))) {
        try {
          const parsed = JSON.parse(str);
          if (Array.isArray(parsed)) return parsed.map(a => String(a).trim()).filter(Boolean);
        } catch { /* fallback */ }
      }
      str = str.replace(/^\[|\]$/g, '').replace(/["']/g, '');
      return str.split(',').map(a => a.trim()).filter(Boolean);
    }
    return amenities.map(a => String(a).trim()).filter(Boolean);
  }
  if (typeof amenities === 'string') {
    let str = amenities.trim();
    if (str.startsWith('[') && str.endsWith(']')) {
      try {
        const parsed = JSON.parse(str);
        if (Array.isArray(parsed)) return parsed.map(a => String(a).trim()).filter(Boolean);
      } catch { /* fallback */ }
    }
    str = str.replace(/^\[|\]$/g, '').replace(/["']/g, '');
    return str.split(',').map(a => a.trim()).filter(Boolean);
  }
  return [];
};

/* skip row when value is empty */
const InfoRow = ({ label, value, mono = false, highlight = false }) => {
  if (value == null || value === '' || value === 'null' || value === 'N/A') return null;
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-400 uppercase tracking-wide w-44 shrink-0 pt-0.5">{label}</span>
      <span className={`text-sm flex-1 break-words ${mono ? 'font-mono' : ''} ${highlight ? 'text-blue-600 font-semibold' : 'text-gray-800 font-medium'}`}>
        {value}
      </span>
    </div>
  );
};

const SectionCard = ({ title, icon, accent = 'blue', children }) => {
  const map = {
    blue:   'border-blue-500   bg-blue-50/60   text-blue-700',
    teal:   'border-teal-500   bg-teal-50/60   text-teal-700',
    violet: 'border-violet-500 bg-violet-50/60 text-violet-700',
    amber:  'border-amber-500  bg-amber-50/60  text-amber-700',
    rose:   'border-rose-500   bg-rose-50/60   text-rose-700',
    slate:  'border-slate-400  bg-slate-50/60  text-slate-700',
    green:  'border-green-500  bg-green-50/60  text-green-700',
  };
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-5">
      <div className={`flex items-center gap-2.5 px-5 py-3.5 border-l-4 ${map[accent] || map.blue}`}>
        <i className={`${icon} text-sm`}></i>
        <h3 className="text-sm font-semibold tracking-wide">{title}</h3>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
};

const PriceRow = ({ label, value, total = false }) => {
  if (value == null) return null;
  return (
    <div className={`flex justify-between items-center py-2.5 ${total ? 'border-t-2 border-gray-200 mt-1 pt-3' : 'border-b border-gray-50'}`}>
      <span className={`text-sm ${total ? 'font-bold text-gray-900' : 'text-gray-500'}`}>{label}</span>
      <span className={`font-semibold ${total ? 'text-lg text-blue-600' : 'text-sm text-gray-800'}`}>{value}</span>
    </div>
  );
};

const ActionBtn = ({ icon, label, onClick, color = 'blue', disabled = false }) => {
  const colors = {
    blue:   'bg-blue-600 hover:bg-blue-700 text-white',
    red:    'bg-red-600 hover:bg-red-700 text-white',
    amber:  'bg-amber-500 hover:bg-amber-600 text-white',
    green:  'bg-emerald-600 hover:bg-emerald-700 text-white',
    slate:  'bg-slate-600 hover:bg-slate-700 text-white',
  };
  return (
    <button onClick={onClick} disabled={disabled}
      className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition shadow-sm ${colors[color]} disabled:opacity-50`}>
      <i className={`${icon} text-xs`}></i>{label}
    </button>
  );
};

const Modal = ({ show, onClose, icon, iconColor, title, children }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
        {icon && (
          <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${iconColor}`}>
            <i className={`${icon} text-2xl`}></i>
          </div>
        )}
        {title && <h3 className="text-base font-semibold text-gray-900 text-center mb-1">{title}</h3>}
        {children}
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <i className="fas fa-times"></i>
        </button>
      </div>
    </div>
  );
};

/* ─── Main Component ──────────────────────────────────────────────────────── */
const CustomerBookingDetail = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [showCancel, setShowCancel]   = useState(false);
  const [showVoucher, setShowVoucher] = useState(false);
  const [showRebook, setShowRebook]   = useState(false);
  const [showTicket, setShowTicket]   = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionLoadingMessage, setActionLoadingMessage] = useState('');
  const [showPDFConfirm, setShowPDFConfirm] = useState(false);

  useEffect(() => { if (bookingId) load(); }, [bookingId]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await agentApiService.getCustomerBookingDetail(bookingId);
      if (res.success) setDetail(res.data);
      else toast.error(res.message || 'Failed to load');
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const doCancel = async () => {
    try {
      setShowCancel(false);
      setActionLoadingMessage('Cancelling hotel booking... Please wait.');
      setActionLoading(true);
      const r = await agentApiService.cancelBooking(bookingId);
      if (r.success) { toast.success('Booking cancelled'); load(); }
      else toast.error(r.message);
    } catch (e) { toast.error(e.message); }
    finally {
      setActionLoading(false);
      setActionLoadingMessage('');
    }
  };

  const doVoucher = async () => {
    try {
      setShowVoucher(false);
      setActionLoadingMessage('Generating voucher... Please wait.');
      setActionLoading(true);
      const r = await agentApiService.generateVoucher(bookingId);
      if (r.success) { toast.success('Voucher generated'); load(); }
      else toast.error(r.message);
    } catch (e) { toast.error(e.message); }
    finally {
      setActionLoading(false);
      setActionLoadingMessage('');
    }
  };

  const doPDF = async () => {
    try {
      setShowPDFConfirm(false);
      setActionLoadingMessage('Generating PDF... Please wait.');
      setActionLoading(true);
      const r = await agentApiService.generateBookingPDF(bookingId);
      if (r.success) toast.success('PDF downloaded');
      else toast.error('PDF generation failed');
    } catch (e) { toast.error(e.message); }
    finally {
      setActionLoading(false);
      setActionLoadingMessage('');
    }
  };

  const doTicket = async (e) => {
    e.preventDefault();
    try {
      setShowTicket(false);
      setActionLoadingMessage('Submitting support ticket... Please wait.');
      setActionLoading(true);
      const r = await agentApiService.createBookingSupportTicket(bookingId, ticketForm);
      if (r.success) { toast.success('Ticket submitted'); setTicketForm({ queryType: 'BOOKING', query: '' }); }
      else toast.error(r.message);
    } catch (e) { toast.error(e.message); }
    finally {
      setActionLoading(false);
      setActionLoadingMessage('');
    }
  };

  /* ── Loading ── */
  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-600 border-t-transparent"></div>
        <p className="text-sm text-gray-400">Loading booking details…</p>
      </div>
    </div>
  );

  /* ── Not found ── */
  if (!detail) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-exclamation-triangle text-2xl text-red-500"></i>
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Booking Not Found</h2>
        <p className="text-sm text-gray-500 mb-5">This booking doesn't exist or you don't have access.</p>
        <button onClick={() => navigate('/agent/dashboard/bookingdetails')}
          className="bg-blue-600 text-white px-5 py-2 rounded-xl text-sm hover:bg-blue-700">
          ← Back to Bookings
        </button>
      </div>
    </div>
  );

  /* ── Destructure ── */
  const { bookingOverview: ov, vendorBookingDetail: vd, fairPlatformFeeAndOffSet: fare, cancellationDetail: cd } = detail;
  const offsetHrs = fare?.cancellationHoursOffset;
  const sm = statusMeta(ov?.status);
  const isCancelled = ov?.status?.toUpperCase() === 'CANCELLED';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">

        {/* ── Top Bar ── */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/agent/dashboard/bookingdetails')}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-300 shadow-sm transition">
              <i className="fas fa-arrow-left text-sm"></i>
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Booking Details</h1>
              <p className="text-xs text-gray-400 mt-0.5 font-mono">{bookingId}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {ov?.status && (
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${sm.cls}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${sm.dot}`}></span>
                {ov.status}
              </span>
            )}
            <button onClick={() => setShowTicket(true)}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-blue-700 shadow-sm transition">
              <i className="fas fa-headset text-xs"></i>Support Ticket
            </button>
          </div>
        </div>

        {/* ── Hero Banner: Hotel + Key Dates ── */}
        {vd && (
          <div className="bg-gradient-to-r from-blue-700 to-indigo-700 rounded-2xl p-6 mb-5 text-white shadow-md">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1">
                {vd.hotel?.hotelName && (
                  <h2 className="text-xl font-bold mb-1">{vd.hotel.hotelName}</h2>
                )}
                {vd.hotel?.starRating > 0 && (
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: vd.hotel.starRating }).map((_, i) => (
                      <i key={i} className="fas fa-star text-yellow-300 text-xs"></i>
                    ))}
                  </div>
                )}
                {(vd.hotel?.addressLine1 || vd.hotel?.city) && (
                  <p className="text-blue-200 text-sm flex items-center gap-1.5">
                    <i className="fas fa-map-marker-alt text-xs"></i>
                    {[vd.hotel.addressLine1, vd.hotel.addressLine2, vd.hotel.city, vd.hotel.countryCode]
                      .filter(Boolean).join(', ')}
                  </p>
                )}
              </div>
              <div className="flex gap-6 sm:text-right">
                {vd.checkInDate && (
                  <div>
                    <p className="text-blue-300 text-xs uppercase tracking-wide mb-0.5">Check-in</p>
                    <p className="text-white font-semibold text-sm">{fmtDate(vd.checkInDate)}</p>
                  </div>
                )}
                {vd.checkOutDate && (
                  <div>
                    <p className="text-blue-300 text-xs uppercase tracking-wide mb-0.5">Check-out</p>
                    <p className="text-white font-semibold text-sm">{fmtDate(vd.checkOutDate)}</p>
                  </div>
                )}
                {vd.noOfRooms > 0 && (
                  <div>
                    <p className="text-blue-300 text-xs uppercase tracking-wide mb-0.5">Rooms</p>
                    <p className="text-white font-semibold text-sm">{vd.noOfRooms}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* ════ LEFT (2 cols) ════ */}
          <div className="lg:col-span-2">

            {/* ── Booking Overview ── */}
            {ov && (
              <SectionCard title="Booking Overview" icon="fas fa-bookmark" accent="blue">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                  <div>
                    <InfoRow label="Booking ID"   value={ov.bookingId}   mono highlight />
                    <InfoRow label="Booking Date" value={fmtDate(ov.bookingDate)} />
                    <InfoRow label="Booking Time" value={ov.bookingTime} />
                  </div>
                  <div>
                    <InfoRow label="Status"     value={ov.status} />
                    <InfoRow label="Refundable" value={ov.isRefundable != null ? (ov.isRefundable ? 'Yes' : 'No') : null} />
                    {ov.isPriceDrop && (
                      <div className="mt-2 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 text-xs px-3 py-1.5 rounded-full">
                        <i className="fas fa-tags text-xs"></i>
                        Price Drop — New Price: {rupee(ov.newPrice)}
                      </div>
                    )}
                  </div>
                </div>
              </SectionCard>
            )}

            {/* ── Vendor Booking Info ── */}
            {vd && (
              <SectionCard title="Vendor Booking Info" icon="fas fa-receipt" accent="teal">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                  <div>
                    <InfoRow label="Provider"            value={vd.provider} />
                    <InfoRow label="Vendor Booking ID"   value={vd.bookingIdAfterBooking} mono />
                    <InfoRow label="Confirmation No"     value={vd.confirmationNo} mono />
                    <InfoRow label="Hotel Conf. No"      value={vd.hotelConfirmationNo} mono />
                    <InfoRow label="No. of Rooms"        value={vd.noOfRooms > 0 ? vd.noOfRooms : null} />
                  </div>
                  <div>
                    <InfoRow label="Last Voucher Date"      value={fmtDate(vd.lastVoucherDate)} />
                    <InfoRow label="Last Cancellation Date" value={fmtCancelDate(vd.lastCancellationDate, offsetHrs)} />
                    <InfoRow label="Guest Nationality"      value={vd.guestNationality} />
                    <InfoRow label="Corporate"              value={vd.isCorporate != null ? (vd.isCorporate ? 'Yes' : 'No') : null} />
                  </div>
                </div>
                {(vd.agentRemarks || vd.specialRequest) && (
                  <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                    <InfoRow label="Agent Remarks"  value={vd.agentRemarks} />
                    <InfoRow label="Special Request" value={vd.specialRequest} />
                  </div>
                )}
                {vd.hotelPolicyDetail && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1.5">Hotel Policy</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{vd.hotelPolicyDetail}</p>
                  </div>
                )}
              </SectionCard>
            )}

            {/* ── Rooms ── */}
            {vd?.rooms?.length > 0 && vd.rooms.map((room, ri) => (
              <SectionCard key={ri}
                title={room.roomTypeName ? `Room ${ri + 1} — ${room.roomTypeName}` : `Room ${ri + 1}`}
                icon="fas fa-bed" accent="violet">

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 mb-3">
                  <div>
                    <InfoRow label="Adults"   value={room.adultCount} />
                    <InfoRow label="Children" value={room.childCount} />
                    <InfoRow label="Inclusion" value={room.inclusion} />
                    <InfoRow label="Smoking"  value={room.smokingPreference} />
                  </div>
                  <div>
                    <InfoRow label="Last Voucher Date"   value={fmtDate(room.lastVoucherDate)} />
                    <InfoRow label="Last Cancellation"   value={fmtCancelDate(room.lastCancellationDate, offsetHrs)} />
                    <InfoRow label="Cancellation Policy" value={room.cancellationPolicy} />
                  </div>
                </div>

                {room.roomDescription && (
                  <div className="text-sm text-gray-700 bg-violet-50/50 border border-violet-100 rounded-xl px-4 py-3 mb-4 leading-relaxed">
                    <p className="text-xs font-semibold text-violet-800 uppercase tracking-wide mb-1">Room Description</p>
                    <div className="prose prose-sm max-w-none text-gray-700 space-y-1" dangerouslySetInnerHTML={{ __html: decodeHtml(room.roomDescription) }} />
                  </div>
                )}

                {/* Amenities */}
                {(() => {
                  const amList = parseAmenities(room.amenities);
                  return amList.length > 0 ? (
                    <div className="mb-4">
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Room Amenities</p>
                      <div className="flex flex-wrap gap-2">
                        {amList.map((a, ai) => (
                          <span key={ai} className="inline-flex items-center gap-1.5 bg-violet-50 text-violet-700 text-xs px-3 py-1 rounded-full border border-violet-100 font-medium shadow-2xs">
                            <i className="fas fa-check-circle text-violet-500 text-xs"></i>{a}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Passengers */}
                {room.passengers?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Passengers</p>
                    <div className="overflow-x-auto rounded-xl border border-gray-100">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                            {['Title','Name','Type','Age','Lead','PAN','Phone','Email'].map(h => (
                              <th key={h} className="px-3 py-2.5 text-left font-medium whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {room.passengers.map((p, pi) => (
                            <tr key={pi} className="hover:bg-gray-50/50">
                              <td className="px-3 py-2.5 whitespace-nowrap">{p.title || '-'}</td>
                              <td className="px-3 py-2.5 whitespace-nowrap font-medium">{[p.firstName, p.lastName].filter(Boolean).join(' ') || '-'}</td>
                              <td className="px-3 py-2.5 whitespace-nowrap">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.paxType === 1 ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                                  {p.paxType === 1 ? 'Adult' : p.paxType === 2 ? 'Child' : '-'}
                                </span>
                              </td>
                              <td className="px-3 py-2.5">{p.age ?? '-'}</td>
                              <td className="px-3 py-2.5">
                                {p.leadPassenger
                                  ? <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">Primary</span>
                                  : <span className="text-gray-400 text-xs">—</span>}
                              </td>
                              <td className="px-3 py-2.5 font-mono text-xs">{p.pan || '-'}</td>
                              <td className="px-3 py-2.5 whitespace-nowrap">{p.phoneNo || '-'}</td>
                              <td className="px-3 py-2.5 whitespace-nowrap">{p.email || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Cancellation Policy Slabs */}
                {room.cancellationPolicies?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Cancellation Policy Slabs</p>
                    <div className="overflow-x-auto rounded-xl border border-gray-100">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 text-xs text-gray-500 uppercase">
                            {['From','To','Charge','Type','Description'].map(h => (
                              <th key={h} className="px-3 py-2.5 text-left font-medium whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {room.cancellationPolicies.map((cp, ci) => (
                            <tr key={ci} className="hover:bg-gray-50/50">
                              <td className="px-3 py-2.5 whitespace-nowrap">{fmtCancelDate(cp.fromDate, offsetHrs) || '-'}</td>
                              <td className="px-3 py-2.5 whitespace-nowrap">{fmtCancelDate(cp.toDate, offsetHrs) || '-'}</td>
                              <td className="px-3 py-2.5 whitespace-nowrap font-semibold">{cp.charge != null ? rupee(cp.charge) || cp.charge : '-'}</td>
                              <td className="px-3 py-2.5 whitespace-nowrap">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cp.chargeType === 0 ? 'bg-slate-100 text-slate-700' : 'bg-orange-100 text-orange-700'}`}>
                                  {cp.chargeType === 0 ? 'Fixed' : cp.chargeType === 1 ? 'Percentage' : '-'}
                                </span>
                              </td>
                              <td className="px-3 py-2.5 text-gray-600">{cp.description || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </SectionCard>
            ))}

            {/* ── Terms & Conditions ── */}
            {(() => {
              const terms = parseAmenities(vd?.termAndCondition);
              return terms.length > 0 ? (
                <SectionCard title="Terms & Conditions" icon="fas fa-file-contract" accent="slate">
                  <ul className="space-y-2">
                    {terms.map((t, i) => (
                      <li key={i} className="text-sm text-gray-700 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100 leading-relaxed">
                        <div dangerouslySetInnerHTML={{ __html: decodeHtml(t) }} />
                      </li>
                    ))}
                  </ul>
                </SectionCard>
              ) : null;
            })()}

            {/* ── Cancellation Detail (only when cancelled) ── */}
            {isCancelled && cd?.cancellationId && (
              <SectionCard title="Cancellation Detail" icon="fas fa-ban" accent="rose">
                <InfoRow label="Cancellation ID"       value={cd.cancellationId} mono />
                <InfoRow label="Cancelled On"          value={fmtDate(cd.cancellationDate)} />
                {cd.cancellationDate && (
                  <InfoRow label="Cancellation Time"
                    value={(() => {
                      try {
                        const d = new Date(cd.cancellationDate);
                        return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
                      } catch { return null; }
                    })()} />
                )}
              </SectionCard>
            )}

            {/* ── Cancellation Policies (all rooms) ── */}
            {vd?.rooms?.some(r => r.cancellationPolicies?.length > 0) && (
              <SectionCard title="Cancellation Policies" icon="fas fa-shield-alt" accent="amber">
                {vd.rooms.map((room, ri) =>
                  room.cancellationPolicies?.length > 0 && (
                    <div key={ri} className={ri > 0 ? 'mt-5 pt-5 border-t border-gray-100' : ''}>
                      {vd.rooms.length > 1 && (
                        <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">
                          {room.roomTypeName ? `Room ${ri + 1} — ${room.roomTypeName}` : `Room ${ri + 1}`}
                        </p>
                      )}
                      <div className="overflow-x-auto rounded-xl border border-gray-100">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="bg-amber-50 text-xs text-amber-700 uppercase">
                              {['From', 'To', 'Charge', 'Type', 'Description'].map(h => (
                                <th key={h} className="px-3 py-2.5 text-left font-medium whitespace-nowrap">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {room.cancellationPolicies.map((cp, ci) => (
                              <tr key={ci} className="hover:bg-amber-50/30">
                                <td className="px-3 py-2.5 whitespace-nowrap">{fmtCancelDate(cp.fromDate, offsetHrs) || '-'}</td>
                                <td className="px-3 py-2.5 whitespace-nowrap">{fmtCancelDate(cp.toDate, offsetHrs) || '-'}</td>
                                <td className="px-3 py-2.5 whitespace-nowrap font-semibold text-gray-800">
                                  {cp.charge != null
                                    ? cp.chargeType === 1 ? `${cp.charge}%` : rupee(cp.charge) ?? cp.charge
                                    : '-'}
                                </td>
                                <td className="px-3 py-2.5 whitespace-nowrap">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cp.chargeType === 0 ? 'bg-slate-100 text-slate-700' : 'bg-orange-100 text-orange-700'}`}>
                                    {cp.chargeType === 0 ? 'Fixed' : cp.chargeType === 1 ? 'Percentage' : '-'}
                                  </span>
                                </td>
                                <td className="px-3 py-2.5 text-gray-600 max-w-xs truncate">{cp.description || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
                )}
              </SectionCard>
            )}
          </div>

          {/* ════ RIGHT Sidebar ════ */}
          <div className="space-y-5">

            {/* ── Fare Summary ── */}
            {fare && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex items-center gap-2.5 px-5 py-3.5 border-l-4 border-purple-500 bg-purple-50/60 text-purple-700">
                  <i className="fas fa-calculator text-sm"></i>
                  <h3 className="text-sm font-semibold tracking-wide">Fare Summary</h3>
                </div>
                <div className="px-5 py-4">
                  <PriceRow label="Total Rooms Price"  value={rupee(fare.totalRoomsPrice)} />
                  <PriceRow label="Platform Fee"       value={rupee(fare.platformFee)} />
                  <PriceRow
                    label={`Tax on Platform Fee${fare.gstRate ? ` (${fare.gstRate}%)` : ''}`}
                    value={rupee(fare.taxOnPlatformFee)}
                  />
                  <PriceRow label="Total Amount" value={rupee(fare.totalPrice)} total />
                  {fare.cancellationHoursOffset != null && (
                    <p className="text-xs text-gray-400 mt-3 flex items-center gap-1.5">
                      <i className="fas fa-clock text-xs"></i>
                      Cancellation dates adjusted by {fare.cancellationHoursOffset} hrs
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ── Booking Price (from overview) ── */}
            {ov && (ov.platformFee || ov.gst || ov.totalPrice) && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex items-center gap-2.5 px-5 py-3.5 border-l-4 border-slate-400 bg-slate-50/60 text-slate-700">
                  <i className="fas fa-file-invoice-dollar text-sm"></i>
                  <h3 className="text-sm font-semibold tracking-wide">Booking Price</h3>
                </div>
                <div className="px-5 py-4">
                  <PriceRow label="Platform Fee" value={rupee(ov.platformFee)} />
                  <PriceRow label="GST"          value={rupee(ov.gst)} />
                  <PriceRow label="Total"        value={rupee(ov.totalPrice)} total />
                </div>
              </div>
            )}

            {/* ── Actions ── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Actions</h3>
              <div className="space-y-2.5">
                {(ov?.status === 'BOOKED' || ov?.status === 'HOLD') && (
                  <ActionBtn icon="fas fa-times-circle" label="Cancel Booking" onClick={() => setShowCancel(true)} color="red" />
                )}
                <ActionBtn icon="fas fa-file-pdf" label="Generate PDF" onClick={() => setShowPDFConfirm(true)} color="blue" />
                {ov?.status === 'HOLD' && (
                  <ActionBtn icon="fas fa-receipt" label="Generate Voucher" onClick={() => setShowVoucher(true)} color="amber" />
                )}
                {ov?.isPriceDrop && (
                  <ActionBtn icon="fas fa-redo" label="Re-Book" onClick={() => setShowRebook(true)} color="green" />
                )}
              </div>
            </div>

            {/* ── Free Cancellation Alert ── */}
            {vd?.lastCancellationDate && !isCancelled && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="fas fa-shield-alt text-emerald-600 text-xs"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">Free Cancellation</p>
                    <p className="text-xs text-emerald-600 mt-0.5">
                      Cancel before {fmtCancelDate(vd.lastCancellationDate, offsetHrs)} at no charge
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ── Last Voucher Date ── */}
            {vd?.lastVoucherDate && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="fas fa-ticket-alt text-blue-600 text-xs"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-blue-800">Voucher Deadline</p>
                    <p className="text-xs text-blue-600 mt-0.5">Generate voucher before {fmtDate(vd.lastVoucherDate)}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ════ Full Screen Loading Overlay ════ */}
        {actionLoading && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[9999] flex flex-col items-center justify-center text-white p-4">
            <div className="bg-white/10 p-6 rounded-2xl border border-white/10 flex flex-col items-center max-w-sm text-center shadow-2xl backdrop-blur-md">
              <div className="relative mb-4 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full border-4 border-blue-400/30 border-t-blue-400 animate-spin"></div>
                <i className="fas fa-spinner animate-pulse text-blue-400 text-xl absolute"></i>
              </div>
              <h4 className="text-lg font-semibold tracking-wide mb-2 text-white">Please Wait</h4>
              <p className="text-sm text-gray-200 font-medium leading-relaxed">
                {actionLoadingMessage || 'Processing your request...'}
              </p>
              <p className="text-xs text-gray-400 mt-3">Do not refresh or close this window.</p>
            </div>
          </div>
        )}

        {/* ════ Modals ════ */}

        {/* Support Ticket */}
        {showTicket && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl relative">
              <button onClick={() => setShowTicket(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                <i className="fas fa-times"></i>
              </button>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <i className="fas fa-headset text-blue-600"></i>
                </div>
                <h3 className="text-base font-semibold text-gray-900">Raise Support Ticket</h3>
              </div>
              <form onSubmit={doTicket} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Booking ID</label>
                  <input type="text" value={bookingId} disabled
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 text-sm font-mono text-gray-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Query Type</label>
                  <select value={ticketForm.queryType} onChange={e => setTicketForm({ ...ticketForm, queryType: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="BOOKING">BOOKING</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Your Query</label>
                  <textarea value={ticketForm.query} onChange={e => setTicketForm({ ...ticketForm, query: e.target.value })}
                    rows={4} required placeholder="Describe your issue…"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none" />
                </div>
                <div className="flex gap-3 pt-1">
                  <button type="submit"
                    className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-blue-700 transition">
                    Submit Ticket
                  </button>
                  <button type="button" onClick={() => setShowTicket(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-200 transition">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Cancel Confirm */}
        {showCancel && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl text-center relative">
              <button onClick={() => setShowCancel(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                <i className="fas fa-times"></i>
              </button>
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-exclamation-triangle text-2xl text-red-500"></i>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Cancel Booking?</h3>
              <p className="text-sm text-gray-500 mb-5">Are you sure you want to cancel this hotel booking? This action cannot be undone.</p>
              <div className="flex gap-3">
                <button onClick={doCancel}
                  className="flex-1 bg-red-600 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-red-700 transition">
                  Yes, Cancel
                </button>
                <button onClick={() => setShowCancel(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-200 transition">
                  Keep Booking
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Voucher Confirm */}
        {showVoucher && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl text-center relative">
              <button onClick={() => setShowVoucher(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                <i className="fas fa-times"></i>
              </button>
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-receipt text-2xl text-amber-500"></i>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Generate Voucher?</h3>
              <p className="text-sm text-gray-500 mb-5">Are you sure you want to generate the voucher for this booking?</p>
              <div className="flex gap-3">
                <button onClick={doVoucher}
                  className="flex-1 bg-amber-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-amber-600 transition">
                  Generate
                </button>
                <button onClick={() => setShowVoucher(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-200 transition">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PDF Confirm */}
        {showPDFConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl text-center relative">
              <button onClick={() => setShowPDFConfirm(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                <i className="fas fa-times"></i>
              </button>
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-file-pdf text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Generate & Download PDF?</h3>
              <p className="text-sm text-gray-500 mb-5">Are you sure you want to generate and download the booking PDF?</p>
              <div className="flex gap-3">
                <button onClick={doPDF}
                  className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-blue-700 transition">
                  Generate PDF
                </button>
                <button onClick={() => setShowPDFConfirm(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-200 transition">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Rebook Confirm */}
        {showRebook && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl text-center relative">
              <button onClick={() => setShowRebook(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                <i className="fas fa-times"></i>
              </button>
              <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-redo text-2xl text-emerald-600"></i>
              </div>
              <h3 className="text-base font-semibold text-gray-900 mb-1">Re-Book Hotel?</h3>
              <p className="text-sm text-gray-500 mb-5">This will open the re-booking page in a new tab.</p>
              <div className="flex gap-3">
                <button onClick={() => { window.open(`/re-booking/${bookingId}`, '_blank'); setShowRebook(false); }}
                  className="flex-1 bg-emerald-600 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-emerald-700 transition">
                  Re-Book
                </button>
                <button onClick={() => setShowRebook(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-200 transition">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default CustomerBookingDetail;
